import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Clock, Loader2, MapPin, Navigation, Plus, Repeat } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money } from "@/lib/api";
import { ridePath, saveRiderSession, useRiderScope } from "@/lib/riderScope";
import { AddressInput } from "@/components/AddressInput";
import { InstallPrompt } from "@/components/InstallPrompt";
import { TripChangePanel } from "@/components/TripChangePanel";
import { StageBadge } from "@/components/StageBadge";
import { RiderPassword } from "@/components/RiderPassword";
import { SavedAddresses } from "@/components/SavedAddresses";
import { Button } from "@/components/ui/button";

const emptyPlace = { address: "", formatted_address: "", place_id: null, lat: null, lng: null };

export default function RiderTrips() {
  const { companyId, token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [stopFor, setStopFor] = useState(null);
  const [stopPlace, setStopPlace] = useState(emptyPlace);
  const [account, setAccount] = useState(null);
  const handle = data?.company?.handle || data?.company?.id || companyId;
  useRiderScope(handle, `trips/${token}`);

  useEffect(() => {
    api.get(`/public/rider-auth/session/${token}`)
      .then(({ data }) => setAccount(data))
      .catch(() => {});
  }, [token]);

  // Opening the emailed link inside the app signs the passenger in and keeps them signed in —
  // otherwise every visit from an email looked like a fresh, logged-out app.
  useEffect(() => {
    const company = data?.company;
    if (!company || !account?.email) return;
    saveRiderSession([company.id, company.handle, companyId],
                     { token, email: account.email, name: account.name || "" });
  }, [data, account, token, companyId]);

  const load = useCallback(
    () => api.get(`/public/rider/${token}`).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e))),
    [token]
  );
  useEffect(() => { load(); }, [load]);

  if (error && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
        <p data-testid="rider-trips-error" className="text-sm text-red-400">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  const { company: c, trips, bookings, email } = data;
  const active = (t) => !["completed", "declined"].includes(t.status);

  return (
    <div data-testid="rider-trips-page" className="min-h-screen bg-ink-900 text-slate-200">
      <header className="glass-header sticky top-0 z-40 border-b border-ink-600">
        <div className="mx-auto flex h-16 max-w-2xl items-center justify-between gap-2 px-4">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold text-slate-100">{c.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gold">My trips</p>
          </div>
          <Link to={ridePath(handle)} data-testid="rider-new-booking" className="shrink-0 font-mono text-xs text-gold">Book a ride →</Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-6 px-4 py-8">
        <p className="text-xs text-slate-500">Signed in as {email}</p>
        <InstallPrompt companyName={c.name} />        {notice && <p data-testid="rider-trips-notice" className="text-sm text-emerald-400">{notice}</p>}
        {error && <p data-testid="rider-trips-inline-error" className="text-sm text-red-400">{error}</p>}

        {bookings.length > 0 && (
          <div className="rounded-xl border border-ink-600 bg-ink-800 p-5">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Awaiting confirmation</p>
            {bookings.map((b) => (
              <div key={b.id} data-testid={`rider-booking-${b.id}`} className="mt-3">
                <p className="text-sm text-slate-300">
                  {b.trip_date} {b.pickup_time} · {b.pickup?.address} → {b.dropoff?.address}
                  {b.estimated_total ? <span className="text-slate-500"> · est. {money(b.estimated_total)}</span> : ""}
                </p>
                <Button variant="outline" data-testid={`rider-booking-cancel-${b.id}`} disabled={busy}
                  onClick={async () => {
                    setBusy(true); setError(""); setNotice("");
                    try {
                      await api.post(`/public/rider/${token}/bookings/${b.id}/cancel`, { reason: "" });
                      setNotice("Request withdrawn.");
                      load();
                    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
                  }}
                  className="mt-2 h-9 border-red-500/40 bg-transparent text-xs text-red-200 hover:bg-red-500/10">
                  Withdraw this request
                </Button>
              </div>
            ))}
          </div>
        )}

        {trips.length === 0 && bookings.length === 0 && (
          <p data-testid="rider-trips-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-8 text-center text-sm text-slate-500">
            No trips yet. Book your first ride and it will show up here.
          </p>
        )}

        {trips.map((t) => (
          <div key={t.id} data-testid={`rider-trip-${t.number}`} className="rounded-xl border border-ink-600 bg-ink-800 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-sm text-gold">TRIP #{t.number}</p>
              <span className="rounded-full border border-ink-600 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                <StageBadge stage={t.stage || "booked"} testId={`rider-stage-${t.number}`}
                label={t.cancelled_by === "passenger" ? "Cancelled by you" : undefined} />
              </span>
            </div>
            <p className="mt-3 text-sm text-slate-200">{t.trip_date} · {t.pickup_time}
              {t.original_pickup_time && t.original_pickup_time !== t.pickup_time
                ? <span className="text-gold"> (moved from {t.original_pickup_time} for your flight)</span> : ""}
            </p>
            <div className="mt-3 space-y-1.5 text-sm text-slate-300">
              <p className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {t.pickup?.address}</p>
              {(t.stops || []).map((s, i) => (
                <p key={i} className="pl-6 text-xs">
                  Stop {i + 1}: {s.address}{" "}
                  <span className={`font-mono text-[10px] uppercase ${s.status === "pending" ? "text-amber-300" : s.status === "declined" ? "text-red-300" : "text-emerald-300"}`}>{s.status}</span>
                </p>
              ))}
              <p className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" /> {t.dropoff?.address}</p>
              <button type="button" data-testid={`rider-ride-again-${t.number}`}
                onClick={() => {
                  sessionStorage.setItem(`ride_again_${c.id}`, JSON.stringify({
                    pickup: t.pickup, dropoff: t.dropoff, passengers: t.passengers,
                    vehicle_id: t.vehicle_id, vehicle_pref: t.vehicle_name,
                  }));
                  navigate(ridePath(handle));
                }}
                className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-ink-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/40 hover:text-gold">
                <Repeat className="h-3 w-3" /> Ride again
              </button>            </div>
            <p className="mt-3 text-xs text-slate-400">
              {t.vehicle_name || "Vehicle TBD"} · {t.chauffeur_name || "chauffeur to be assigned"} ·{" "}
              <span className="text-slate-100">{t.final_total ? `total ${money(t.final_total)}` : `estimate ${money(t.total_charge)}`}</span>
              {t.balance_due ? <span className="text-gold"> · balance {money(t.balance_due)}</span> : ""}
            </p>

            {active(t) && (
              <div className="mt-5 space-y-3 border-t border-ink-600 pt-4">
                <div className="flex flex-wrap gap-2">
                  {t.track_token && (
                    <Button asChild data-testid={`rider-track-${t.number}`} className="bg-gold text-ink-900 hover:bg-gold-light">
                      <Link to={ridePath(handle, `track/${t.track_token}`)}><Clock className="mr-2 h-4 w-4" /> Track my ride</Link>
                    </Button>
                  )}
                  {t.pickup?.lat != null && (
                    <Button asChild variant="outline" data-testid={`rider-map-${t.number}`} className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                      <a href={`https://www.google.com/maps/search/?api=1&query=${t.pickup.lat},${t.pickup.lng}`} target="_blank" rel="noopener noreferrer">
                        <Navigation className="mr-2 h-4 w-4" /> Pickup on map
                      </a>
                    </Button>
                  )}
                </div>
                {stopFor === t.id ? (                  <div className="space-y-3">
                    <AddressInput label="New stop" testId={`rider-stop-input-${t.number}`} companyId={c?.id} value={stopPlace}
                      onChange={setStopPlace} placeholder="Restaurant, office, hotel…" />
                    {t.stop_fee > 0 && (
                      <p data-testid={`rider-stop-fee-${t.number}`} className="rounded-lg border border-gold/30 bg-gold/10 p-3 text-xs text-gold">
                        Adding a stop costs {money(t.stop_fee)}. You'll go straight to payment once you confirm.
                      </p>
                    )}
                    <div className="relative z-[950] flex gap-2">
                      <Button data-testid={`rider-stop-submit-${t.number}`} disabled={busy} className="bg-gold text-ink-900 hover:bg-gold-light"
                        onClick={async () => {
                          setBusy(true); setError(""); setNotice("");
                          try {
                            const { data: res } = await api.post(`/public/rider/${token}/trips/${t.id}/stops`, stopPlace);
                            setStopFor(null); setStopPlace(emptyPlace); load();
                            if (res.pay_token) {
                              navigate(ridePath(handle, `pay/${res.pay_token}`));
                              return;
                            }
                            setNotice(res.stop.status === "pending"
                              ? res.charge > 0
                                ? `Stop requested — dispatch will confirm it shortly and ${money(res.charge)} will be added to your trip.`
                                : "Stop requested — dispatch will confirm it shortly."
                              : res.charge > 0
                                ? `Stop added — ${money(res.charge)} was added to your trip total.`
                                : "Stop added to your itinerary.");
                          } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
                        }}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                          : t.stop_fee > 0 ? `Add stop · ${money(t.stop_fee)}` : "Request stop"}
                      </Button>
                      <Button variant="ghost" className="text-slate-400" onClick={() => setStopFor(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  t.change_window?.can_change && (
                    <Button data-testid={`rider-add-stop-${t.number}`} variant="outline"
                      onClick={() => { setStopFor(t.id); setStopPlace(emptyPlace); }}
                      className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                      <Plus className="mr-2 h-4 w-4" /> Add a stop to this ride
                    </Button>
                  )
                )}
                <TripChangePanel token={token} trip={t}
                  onDone={() => { setNotice("Done — dispatch has been notified and your confirmation is on its way."); load(); }}
                  onError={setError} />
              </div>
            )}
          </div>
        ))}

        <SavedAddresses token={token} />
        <RiderPassword token={token} hasPassword={account?.has_password}
          canSet={account?.can_set_password !== false}
          onSet={() => setAccount((a) => ({ ...(a || {}), has_password: true }))} />

        {c.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </main>
    </div>
  );
}
